import React, { useState, useEffect, useCallback } from 'react';
import { familyAPI, userAPI } from '../../services/api';
import { Family, User } from '../../types';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { IconButton } from '../common/IconButton';
import { PencilIcon, TrashIcon, CheckIcon, XIcon } from '../common/icons';

// Reusable member picker (used in create + edit).
const MemberPicker: React.FC<{
  users: User[];
  selected: string[];
  onToggle: (id: string) => void;
}> = ({ users, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {users.map((u) => {
      const on = selected.includes(u._id);
      return (
        <button key={u._id} type="button" onClick={() => onToggle(u._id)}
          className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
            on ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
          }`}>
          {u.username}
        </button>
      );
    })}
  </div>
);

interface Props { usersVersion: number; }

const FamiliesSection: React.FC<Props> = ({ usersVersion }) => {
  const [families, setFamilies] = useState<Family[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);

  // create form
  const [name, setName] = useState('');
  const [newMembers, setNewMembers] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  // edit state (one family at a time)
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editMembers, setEditMembers] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const loadFamilies = useCallback(() => {
    familyAPI.getAll().then((r) => setFamilies(r.data)).catch(() => setError('Erreur de chargement'));
  }, []);

  useEffect(() => { loadFamilies(); }, [loadFamilies]);
  useEffect(() => { userAPI.getAll().then((r) => setUsers(r.data)).catch(() => {}); }, [usersVersion]);

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await familyAPI.create({ name, members: newMembers });
      setName(''); setNewMembers([]);
      loadFamilies();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (f: Family) => {
    setEditId(f._id);
    setEditName(f.name);
    setEditMembers(f.members.map((m) => m._id));
  };

  const saveEdit = async () => {
    try {
      await familyAPI.update(editId!, { name: editName, members: editMembers });
      setEditId(null);
      loadFamilies();
    } catch {
      setError('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await familyAPI.delete(id);
      setConfirmDelete(null);
      loadFamilies();
    } catch {
      setError('Erreur lors de la suppression');
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Create */}
      <form onSubmit={handleCreate} className="flex flex-col gap-3 bg-gray-50 rounded-xl p-4">
        <Input label="Nouvelle famille" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="ex. Couple, Parents…" required fullWidth />
        {users.length > 0 && <MemberPicker users={users} selected={newMembers} onToggle={(id) => setNewMembers((m) => toggle(m, id))} />}
        <div><Button type="submit" variant="primary" loading={creating} disabled={!name}>Créer la famille</Button></div>
      </form>

      {/* List */}
      <div className="flex flex-col gap-3">
        {families.map((f) => (
          <div key={f._id} className="border border-gray-100 rounded-xl p-4">
            {editId === f._id ? (
              <div className="flex flex-col gap-3">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} fullWidth />
                <MemberPicker users={users} selected={editMembers} onToggle={(id) => setEditMembers((m) => toggle(m, id))} />
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="success" onClick={saveEdit} disabled={!editName}>Enregistrer</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditId(null)}>Annuler</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-semibold text-gray-900">{f.name}</span>
                <div className="flex flex-wrap gap-1.5">
                  {f.members.length === 0
                    ? <span className="text-sm text-gray-400">Aucun membre</span>
                    : f.members.map((m) => (
                        <span key={m._id} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{m.username}</span>
                      ))}
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {confirmDelete === f._id ? (
                    <>
                      <span className="text-sm text-gray-500">Supprimer la famille et ses listes&nbsp;?</span>
                      <IconButton type="button" size="sm" variant="danger" ariaLabel="Confirmer"
                        onClick={() => handleDelete(f._id)}><CheckIcon /></IconButton>
                      <IconButton type="button" size="sm" variant="ghost" ariaLabel="Annuler"
                        onClick={() => setConfirmDelete(null)}><XIcon /></IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton type="button" size="sm" variant="ghost" ariaLabel="Modifier la famille"
                        title="Modifier" onClick={() => startEdit(f)}><PencilIcon /></IconButton>
                      <IconButton type="button" size="sm" variant="ghost" ariaLabel="Supprimer la famille"
                        title="Supprimer" onClick={() => setConfirmDelete(f._id)}
                        className="hover:!bg-danger/10 hover:!text-danger"><TrashIcon /></IconButton>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FamiliesSection;
